import type { AdminOrderResponse } from '@inflip/contracts/admin';
import type { LicenseSuspension } from '@inflip/contracts/user';
import { LicenseStatus, OrderStatus } from '@inflip/shared';
import { useId, useState } from 'react';

import { Button, Dialog, DialogFooter, ErrorBanner, inputCls } from './ui';
import { api } from '../lib/api-client';
import { formatDateTime } from '../lib/time-zone';

/** 중지할 수 있는 주문 상태 — 서버(AMENDABLE_ORDER_STATUSES)와 같다 */
const SUSPENDABLE: string[] = [
  OrderStatus.LICENSE_ISSUED,
  OrderStatus.SETTLEMENT_PENDING,
  OrderStatus.PAYOUT_COMPLETED,
  OrderStatus.DISPUTED,
];

const LICENSE_END_LABEL: Partial<Record<LicenseStatus, string>> = {
  [LicenseStatus.EXPIRED]: '계약 기간이 끝나 함께 끝남',
  [LicenseStatus.REVOKED]: '환불(회수)로 함께 끝남',
  [LicenseStatus.TERMINATED]: '계약 해지로 함께 끝남',
};

/**
 * 라이선스 사용 중지 — 조사·분쟁 중에 사용을 잠시 멈추고, 결론이 나면 푼다. 돈·계약은 그대로다.
 * 결론이 해지·환불이면 그 처리로 넘어간다 (중지 중에도 할 수 있다). 걸고 푼 이력을 보여 준다.
 */
export function LicenseSuspensionSection({
  order,
  token,
  onChanged,
}: {
  order: AdminOrderResponse;
  token: string;
  onChanged: () => Promise<unknown>;
}) {
  const [dialog, setDialog] = useState<'suspend' | LicenseSuspension | null>(null);
  const license = order.license;
  if (!license) return null;

  const suspensions = order.licenseSuspensions ?? [];
  const open = suspensions.find((s) => !s.resumedAt) ?? null;
  const suspended = license.status === LicenseStatus.SUSPENDED;
  const suspendable = license.status === LicenseStatus.ACTIVE && SUSPENDABLE.includes(order.status);

  return (
    <div className="py-3 text-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="text-muted-foreground">사용 중지</p>
        {suspendable && (
          <Button size="sm" variant="secondary" onClick={() => setDialog('suspend')}>
            사용 중지
          </Button>
        )}
        {suspended && open && (
          <Button size="sm" variant="secondary" onClick={() => setDialog(open)}>
            사용 중지 풀기
          </Button>
        )}
      </div>

      {suspended && open && (
        <div className="mt-2 rounded-lg bg-rose-50/70 p-3.5">
          <p className="font-bold text-rose-600">
            사용 중지 중{' '}
            <span className="font-medium text-muted-foreground">
              · {formatDateTime(open.suspendedAt)}부터
            </span>
          </p>
          <p className="mt-1 text-xs text-muted-foreground">사유: {open.reason}</p>
          <p className="mt-1 text-xs text-muted-foreground">
            자동 구매확정이 멈춰 있어요. 풀면 구매확정 전인 계약은 중지된 기간만큼 기한이 늦춰져요.
            결론이 해지·환불이면 중지를 풀지 않고 그 처리를 하면 돼요.
          </p>
        </div>
      )}
      {!suspended && suspensions.length === 0 && !suspendable && (
        <p className="mt-1.5 text-xs text-muted-foreground">
          사용 중인 라이선스만 중지할 수 있어요.
        </p>
      )}

      {suspensions.filter((s) => s !== open || !suspended).length > 0 && (
        <ul className="mt-2 divide-y divide-border/60">
          {suspensions
            .filter((s) => s !== open || !suspended)
            .map((s) => (
              <li key={s.id} className="py-2.5 text-xs">
                <p>
                  <span className="font-bold text-muted-foreground">
                    {s.resumedAt ? '풀림' : (LICENSE_END_LABEL[license.status] ?? '끝남')}
                  </span>{' '}
                  <span className="text-muted-foreground">
                    · 중지 {formatDateTime(s.suspendedAt)}
                    {s.resumedAt && ` · 해제 ${formatDateTime(s.resumedAt)}`}
                  </span>
                </p>
                <p className="mt-0.5 text-muted-foreground">사유: {s.reason}</p>
                {s.resumeNote && (
                  <p className="mt-0.5 text-muted-foreground">해제 메모: {s.resumeNote}</p>
                )}
              </li>
            ))}
        </ul>
      )}

      {dialog === 'suspend' && (
        <SuspendDialog
          order={order}
          token={token}
          onClose={() => setDialog(null)}
          onDone={async () => {
            await onChanged();
            setDialog(null);
          }}
          onFailed={onChanged}
        />
      )}
      {dialog && dialog !== 'suspend' && (
        <ResumeDialog
          suspension={dialog}
          token={token}
          onClose={() => setDialog(null)}
          onDone={async () => {
            await onChanged();
            setDialog(null);
          }}
          onFailed={onChanged}
        />
      )}
    </div>
  );
}

function SuspendDialog({
  order,
  token,
  onClose,
  onDone,
  onFailed,
}: {
  order: AdminOrderResponse;
  token: string;
  onClose: () => void;
  onDone: () => Promise<unknown>;
  onFailed: () => Promise<unknown>;
}) {
  const id = useId();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const license = order.license!;

  async function submit() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.suspendLicense(token, order.id, {
        reason: reason.trim(),
        seenLicenseUpdatedAt: license.updatedAt,
      });
      // 지금 상태가 그려진 뒤에 창을 닫는다
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await onFailed();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog label="라이선스 사용 중지" title="라이선스 사용을 중지할까요?" onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        풀 때까지 구매자는 이 라이선스를 쓸 수 없고, 증서에도 사용 중지로 표시돼요. 돈과 계약은
        그대로예요. 구매자·판매자에게 알림과 채팅으로 사유를 알려요.
      </p>
      <label htmlFor={id} className="mt-5 block text-sm font-bold">
        중지 사유
      </label>
      <textarea
        id={id}
        rows={3}
        maxLength={1000}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="예: 금지 업종 사용 신고 확인 중 (10/17)"
        className={`mt-2 w-full resize-none py-2 ${inputCls}`}
      />
      <p className="mt-1 text-xs text-muted-foreground">
        구매자·판매자에게 그대로 보여요 (5자 이상).
      </p>
      <DialogFooter notice={error ? <ErrorBanner message={error} /> : undefined}>
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        <Button
          className="flex-1"
          disabled={busy || reason.trim().length < 5}
          onClick={() => void submit()}
        >
          {busy ? '중지하는 중…' : '사용 중지'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}

function ResumeDialog({
  suspension,
  token,
  onClose,
  onDone,
  onFailed,
}: {
  suspension: LicenseSuspension;
  token: string;
  onClose: () => void;
  onDone: () => Promise<unknown>;
  onFailed: () => Promise<unknown>;
}) {
  const id = useId();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.resumeLicense(token, suspension.id, note.trim() ? { note: note.trim() } : {});
      // 지금 상태가 그려진 뒤에 창을 닫는다
      await onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      await onFailed();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog label="사용 중지 풀기" title="사용 중지를 풀까요?" onClose={onClose}>
      <p className="text-sm text-muted-foreground">
        구매자가 계약 내용대로 다시 쓸 수 있어요. 구매확정 전이면 중지된 기간만큼 구매확정 기한이
        늦춰져요. 그사이 계약 기간이 끝났으면 바로 만료돼요.
      </p>
      <p className="mt-3 text-xs text-muted-foreground">중지 사유: {suspension.reason}</p>
      <label htmlFor={id} className="mt-5 block text-sm font-bold">
        해제 메모 <span className="font-medium text-muted-foreground">(선택)</span>
      </label>
      <textarea
        id={id}
        rows={3}
        maxLength={1000}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder="예: 확인 결과 계약 범위 안의 사용"
        className={`mt-2 w-full resize-none py-2 ${inputCls}`}
      />
      <p className="mt-1 text-xs text-muted-foreground">구매자·판매자에게 보여요.</p>
      <DialogFooter notice={error ? <ErrorBanner message={error} /> : undefined}>
        <Button variant="secondary" className="flex-1" onClick={onClose}>
          취소
        </Button>
        <Button className="flex-1" disabled={busy} onClick={() => void submit()}>
          {busy ? '푸는 중…' : '사용 중지 풀기'}
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
